import React, { useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { Text, Stack, HStack, FormLabel, Grid } from '@chakra-ui/react';
import { InputText } from '../../../components/Input';
import TemplateSelect from './TemplateSelect';
import QuestionCardCustom from '../../../components/Card/Question-card-custom';
import { PillarSelect } from '../../../components/Catalog/CatalogSelect';

const PillarForm = React.memo(
 ({
  index,
  field,
  register,
  errors,
  control,
  trigger,
  setValue,
  templates,
  templatesIsLoading,
 }) => {
  // EG-40: espelhado no form para o seletor reagir; field não atualiza sozinho.
  const [idPillar, setIdPillar] = useState(field?.idPillar ?? null);
  const numberOfThemes = parseInt(field?.numberOfThemes) || 0;
  const numberOfLevels = parseInt(field?.numberOfLevels) || 0;

  const {
   fields: themes,
   append: appendThemes,
   remove: removeThemes,
  } = useFieldArray({
   control,
   name: `form.${index}.themes`,
  });

  const {
   fields: levels,
   append: appendLevels,
   remove: removeLevels,
   update: updateLevels,
  } = useFieldArray({
   control,
   name: `form.${index}.levels`,
  });

  const manageThemes = () => {
   const diff = numberOfThemes - themes.length;
   if (diff > 0) {
    Array.from({ length: diff }).forEach(() => appendThemes({ title: '' }));
   } else if (diff < 0) {
    Array.from({ length: Math.abs(diff) }).forEach(() => removeThemes(themes.length - 1));
   }
  };

  const manageLevels = () => {
   const diff = numberOfLevels - levels.length;
   if (diff > 0) {
    // Corrigido: usar o índice atual + 1 para definir o nivel
    Array.from({ length: diff }).forEach((_, index) =>
     appendLevels({
      nivel: levels.length + index + 1, // Agora o nivel será incrementado corretamente
      questions: [],
     })
    );
   } else if (diff < 0) {
    Array.from({ length: Math.abs(diff) }).forEach(() => removeLevels(levels.length - 1));
   }
  };

  useEffect(manageThemes, [numberOfThemes, themes, appendThemes, removeThemes]);
  useEffect(manageLevels, [numberOfLevels, levels, appendLevels, removeLevels]);

  const removeQuestion = (level, questionId) => {
   const levelIndex = level - 1;
   const updatedQuestions = levels[levelIndex]?.questions?.filter(
    (question) => question.id_question_template !== questionId
   );
   updateLevels(levelIndex, {
    ...levels[levelIndex],
    questions: updatedQuestions,
   });
  };

  return (
   <Stack p='6' spacing='25px'>
    {/* EG-40: o pilar deixou de ser digitado aqui e passa a ser escolhido do
        catálogo global. Texto, imagem e helpers saíram desta tela: são
        conteúdo do pilar, não do questionário, e se editam em /pillars.
        Enquanto isso era texto livre, "Governança" e "Governança
        empresarial" nasciam como conceitos distintos. */}
    <Stack w='432px'>
     <PillarSelect
      id={`form-${index}-id_pillar`}
      value={idPillar}
      onChange={(escolhido) => {
       setIdPillar(escolhido);
       setValue(`form.${index}.idPillar`, escolhido);
       trigger(`form.${index}.idPillar`);
      }}
     />
    </Stack>
    <Stack spacing='4'>
     <Text fontWeight={600}>Temas</Text>
     <Grid templateColumns='repeat(2, 1fr)' gap='48px' rowGap='4'>
      {themes?.map((theme, themeIndex) => (
       <HStack key={theme.id} spacing='6'>
        <FormLabel
         htmlFor={`form.${index}.themes.${themeIndex}.title`}
         color='#5C6066'
         fontSize='sm'
         fontWeight={600}
         whiteSpace='nowrap'
         m={0}
        >
         Tema {themeIndex + 1}
        </FormLabel>
        <InputText
         {...register(`form.${index}.themes.${themeIndex}.title`)}
         error={errors?.form?.[index]?.themes?.[themeIndex]?.themeName}
         placeholder='Digite o nome do tema'
         labelSize='sm'
        />
       </HStack>
      ))}
     </Grid>
    </Stack>
    {Array.from({ length: numberOfLevels }, (_, i) => (
     <Stack key={i} spacing={6}>
      <HStack h='51px' bg='#EAEEF0' p='16px'>
       <Text color='#5C6066' fontWeight={600}>
        Perguntas - Nível {i + 1}
       </Text>
      </HStack>
      <TemplateSelect
       level={i + 1}
       levels={levels}
       templates={templates}
       templatesIsLoading={templatesIsLoading}
       selectedQuestions={levels[i]?.questions || []}
       setSelectedQuestions={(newQuestions) =>
        updateLevels(i, { ...levels[i], questions: newQuestions })
       }
      />
      {levels[i]?.questions?.map((question, idx) => (
       <QuestionCardCustom
        key={question?.id_question_template}
        index={idx}
        remove={() => removeQuestion(i + 1, question?.id_question_template)}
        question={question}
       />
      ))}
     </Stack>
    ))}
   </Stack>
  );
 }
);

export default PillarForm;
